import { useState } from 'react';
import { adminApi } from '../../api/admin';
import { useAsync, Banner, Item, Empty } from './ui';
import { Modal } from '../../components/Modal';

type Row = Record<string, unknown>;
const blank = {
  scope: 'template', scopeId: 'HT_LT_TRANSFORMER', fieldKey: '',
  normalMin: '', normalMax: '', warnMin: '', warnMax: '', criticalMin: '', criticalMax: '',
};
const cols = ['normalMin', 'normalMax', 'warnMin', 'warnMax', 'criticalMin', 'criticalMax'] as const;
const range = (a: unknown, b: unknown) => `${a === '' || a == null ? '–' : a} … ${b === '' || b == null ? '–' : b}`;

export default function ThresholdsAdmin() {
  const { data, error, loading, reload } = useAsync(() => adminApi.listThresholds(), [], 'thresholds');
  const [form, setForm] = useState<typeof blank | null>(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form) return;
    setSaving(true); setMsg('');
    try { await adminApi.saveThreshold(form); setForm(null); reload(); }
    catch (e) { setMsg(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  }
  async function del(r: Row) {
    if (!confirm('Delete this threshold?')) return;
    try { await adminApi.deleteThreshold({ scope: r['Scope'], scopeId: r['Scope ID'], fieldKey: r['Field Key'] }); reload(); }
    catch (e) { setMsg(e instanceof Error ? e.message : 'Delete failed'); }
  }

  return (
    <div>
      <div className="spread">
        <h1>Abnormal thresholds</h1>
        <button className="sm" onClick={() => { setForm({ ...blank }); setMsg(''); }}>+ Add</button>
      </div>
      <p className="muted">
        Outside the critical range → flagged <strong>critical</strong>; outside the warning or
        normal range → <strong>warning</strong>. Blank = not checked. Equipment rules override template rules.
      </p>
      <Banner error={error || msg} />
      {loading && <div className="spinner" />}
      {data && data.rows.length === 0 && <Empty icon="🎚️">No thresholds configured.</Empty>}

      <div className="cardlist">
        {data?.rows.map((r: Row, i) => (
          <Item
            key={i}
            title={<>{String(r['Field Key'])} <span className="muted" style={{ fontWeight: 400, fontSize: '.82rem' }}>· {String(r['Scope'])} {String(r['Scope ID'])}</span></>}
            meta={<>
              <span>normal {range(r['Normal Min'], r['Normal Max'])}</span>
              <span style={{ color: 'var(--warn)' }}>warn {range(r['Warn Min'], r['Warn Max'])}</span>
              <span style={{ color: 'var(--bad)' }}>crit {range(r['Critical Min'], r['Critical Max'])}</span>
            </>}
            actions={<>
              <button className="secondary xs" onClick={() => setForm({
                scope: String(r['Scope'] ?? 'template'), scopeId: String(r['Scope ID'] ?? ''), fieldKey: String(r['Field Key'] ?? ''),
                normalMin: String(r['Normal Min'] ?? ''), normalMax: String(r['Normal Max'] ?? ''),
                warnMin: String(r['Warn Min'] ?? ''), warnMax: String(r['Warn Max'] ?? ''),
                criticalMin: String(r['Critical Min'] ?? ''), criticalMax: String(r['Critical Max'] ?? ''),
              })}>Edit</button>
              <button className="secondary xs" onClick={() => del(r)}>Del</button>
            </>}
          />
        ))}
      </div>

      {form && (
        <Modal
          title="Threshold"
          onClose={() => !saving && setForm(null)}
          footer={<>
            <button className="secondary" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
            <button onClick={save} disabled={saving || !form.scopeId || !form.fieldKey}>{saving ? 'Saving…' : 'Save'}</button>
          </>}
        >
          <div className="grid3">
            <div>
              <label>Scope</label>
              <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                <option value="template">template</option>
                <option value="equipment">equipment</option>
              </select>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label>{form.scope === 'template' ? 'Template ID' : 'Equipment ID'}</label>
              <input value={form.scopeId} onChange={(e) => setForm({ ...form, scopeId: e.target.value })} />
            </div>
          </div>
          <label>Field key</label>
          <input value={form.fieldKey} onChange={(e) => setForm({ ...form, fieldKey: e.target.value })} placeholder="tr1_voltage" />
          <div className="grid3" style={{ marginTop: 8 }}>
            {cols.map((c) => (
              <div key={c}>
                <label>{c.replace('Min', ' min').replace('Max', ' max')}</label>
                <input inputMode="decimal" value={form[c]} onChange={(e) => setForm({ ...form, [c]: e.target.value })} />
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
